/**
 * CR305: Recipes Studio data hook
 * Handles CRUD for capture_recipe_templates (generalized with recipe_scope)
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Recipe, RecipeScope } from '@/lib/recipes-studio/types';

export function useRecipesStudio(defaultScope: RecipeScope = 'WEB_CRAWL') {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scopeFilter, setScopeFilter] = useState<RecipeScope | 'ALL'>(defaultScope);

  const selectedRecipe = recipes.find(r => r.id === selectedId) ?? null;

  const loadRecipes = useCallback(async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('capture_recipe_templates')
        .select('*')
        .eq('is_archived', false)
        .order('recipe_name');

      if (scopeFilter !== 'ALL') {
        query = query.eq('recipe_scope', scopeFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      const typed = (data || []) as unknown as Recipe[];
      setRecipes(typed);

      // Auto-select first if current selection is gone
      if (selectedId && !typed.find(r => r.id === selectedId)) {
        setSelectedId(typed[0]?.id ?? null);
      } else if (!selectedId && typed.length > 0) {
        setSelectedId(typed[0].id);
      }
    } catch (err) {
      console.error('[CR305] Load recipes error:', err);
      toast.error('Failed to load recipes');
    } finally {
      setIsLoading(false);
    }
  }, [scopeFilter, selectedId]);

  useEffect(() => {
    loadRecipes();
  }, [scopeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const createRecipe = useCallback(async (name: string, fromTemplateId?: string) => {
    try {
      let insertPayload: Record<string, unknown> = {
        recipe_name: name,
        recipe_scope: scopeFilter === 'ALL' ? 'WEB_CRAWL' : scopeFilter,
        provider: 'firecrawl',
        recipe_config: {},
        is_system_template: false,
        is_template: false,
        is_archived: false,
        steps: [] as never,
        recipe_defaults: {} as never,
        success_criteria: {} as never,
      };

      // CR310: If creating from a template, clone its config fields
      if (fromTemplateId) {
        const template = recipes.find(r => r.id === fromTemplateId);
        if (template) {
          insertPayload = {
            ...insertPayload,
            recipe_scope: template.recipe_scope,
            provider: template.provider,
            recipe_config: template.recipe_config as never,
            steps: template.steps as never,
            recipe_defaults: template.recipe_defaults as never,
            success_criteria: template.success_criteria as never,
            // Clear start URL for multi-merchant reuse
            default_start_url: null,
          };
        }
      }

      const { data, error } = await supabase
        .from('capture_recipe_templates')
        .insert([insertPayload as never])
        .select()
        .single();

      if (error) throw error;
      const newRecipe = data as unknown as Recipe;
      setRecipes(prev => [...prev, newRecipe].sort((a, b) => a.recipe_name.localeCompare(b.recipe_name)));
      setSelectedId(newRecipe.id);
      const suffix = fromTemplateId ? ' (from template)' : '';
      toast.success(`Created "${name}"${suffix}`);
      return newRecipe;
    } catch (err) {
      console.error('[CR305] Create error:', err);
      toast.error('Failed to create recipe');
      return null;
    }
  }, [scopeFilter, recipes]);

  const cloneRecipe = useCallback(async (recipeId: string) => {
    const source = recipes.find(r => r.id === recipeId);
    if (!source) return null;

    try {
      const { data, error } = await supabase
        .from('capture_recipe_templates')
        .insert([{
          recipe_name: `${source.recipe_name} (copy)`,
          description: source.description,
          viewer_type: source.viewer_type,
          default_start_url: source.default_start_url,
          steps: source.steps as never,
          recipe_defaults: source.recipe_defaults as never,
          success_criteria: source.success_criteria as never,
          recipe_scope: source.recipe_scope,
          provider: source.provider,
          recipe_config: source.recipe_config as never,
          is_system_template: false,
          is_template: false,
          is_archived: false,
        }])
        .select()
        .single();

      if (error) throw error;
      const cloned = data as unknown as Recipe;
      setRecipes(prev => [...prev, cloned].sort((a, b) => a.recipe_name.localeCompare(b.recipe_name)));
      setSelectedId(cloned.id);
      toast.success(`Cloned as "${cloned.recipe_name}"`);
      return cloned;
    } catch (err) {
      console.error('[CR305] Clone error:', err);
      toast.error('Failed to clone recipe');
      return null;
    }
  }, [recipes]);

  const archiveRecipe = useCallback(async (recipeId: string) => {
    try {
      const { error } = await supabase
        .from('capture_recipe_templates')
        .update({ is_archived: true } as never)
        .eq('id', recipeId);

      if (error) throw error;
      setRecipes(prev => prev.filter(r => r.id !== recipeId));
      if (selectedId === recipeId) {
        setSelectedId(recipes.find(r => r.id !== recipeId)?.id ?? null);
      }
      toast.success('Recipe archived');
    } catch (err) {
      console.error('[CR305] Archive error:', err);
      toast.error('Failed to archive recipe');
    }
  }, [recipes, selectedId]);

  const updateRecipe = useCallback(async (recipeId: string, updates: Partial<Recipe>) => {
    setSaving(true);
    try {
      // Strip read-only fields
      const { id, created_at, updated_at, ...safeUpdates } = updates as Record<string, unknown>;
      
      const { error } = await supabase
        .from('capture_recipe_templates')
        .update(safeUpdates as never)
        .eq('id', recipeId);

      if (error) throw error;

      setRecipes(prev => prev.map(r => 
        r.id === recipeId ? { ...r, ...updates, updated_at: new Date().toISOString() } : r
      ));
      toast.success('Recipe saved');
    } catch (err) {
      console.error('[CR305] Update error:', err);
      toast.error('Failed to save recipe');
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    recipes,
    selectedId,
    selectedRecipe,
    isLoading,
    saving,
    scopeFilter,
    setScopeFilter,
    setSelectedId,
    loadRecipes,
    createRecipe,
    cloneRecipe,
    archiveRecipe,
    updateRecipe,
  };
}
